/**
 * @file OnboardingPlanDialog.tsx
 * @description Dialog shown when a new plan is generated while an existing plan is active.
 * This can happen when:
 * 1. A user re-runs onboarding while already having a plan
 * 2. A user completes onboarding then signs into an account with an existing cloud plan
 * Offers a choice: keep the existing plan or adopt the newly generated one.
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from '@/components/ui/button';
import { RefreshCw, Shield, Settings, Sparkles } from 'lucide-react';
import { useStore } from '@/lib/store';
import type { WeekSchedule, TrainingBlock } from '@/types';

const PENDING_PLAN_KEY = 'ae-pending-onboarding-plan';
const PENDING_BLOCKS_KEY = 'ae-pending-onboarding-blocks';

export function OnboardingPlanDialog() {
    const [open, setOpen] = useState(false);
    const setPlan = useStore(state => state.setPlan);
    const setBlocks = useStore(state => state.setBlocks);
    const currentPlan = useStore(state => state.plan);
    const location = useLocation();

    // Check for pending plan on mount AND on navigation (handles re-onboarding redirect)
    useEffect(() => {
        const pending = sessionStorage.getItem(PENDING_PLAN_KEY);
        if (pending) {
            // Auto-accept if no existing plan (first time user)
            if (!currentPlan || currentPlan.length === 0) {
                // Use a small timeout to let store rehydration finish if needed, 
                // but generally we want to just set it immediately.
                // However, we can't call state setter in render, so we do it in effect.
                try {
                    const plan: WeekSchedule[] = JSON.parse(pending);
                    setPlan(plan);

                    const blocksRaw = sessionStorage.getItem(PENDING_BLOCKS_KEY);
                    if (blocksRaw) {
                        const blocks: TrainingBlock[] = JSON.parse(blocksRaw);
                        setBlocks(blocks);
                    }

                    sessionStorage.removeItem(PENDING_PLAN_KEY);
                    sessionStorage.removeItem(PENDING_BLOCKS_KEY);
                } catch (e) {
                    console.error('Failed to auto-restore onboarding plan:', e);
                }
                return;
            }

            // Small delay to let the main UI settle
            const timer = setTimeout(() => setOpen(true), 800);
            return () => clearTimeout(timer);
        }
    }, [location.pathname, currentPlan, setPlan, setBlocks]);

    const handleKeepExisting = () => {
        sessionStorage.removeItem(PENDING_PLAN_KEY);
        sessionStorage.removeItem(PENDING_BLOCKS_KEY);
        setOpen(false);
    };

    const handleUseNewPlan = () => {
        try {
            const planRaw = sessionStorage.getItem(PENDING_PLAN_KEY);
            if (planRaw) {
                const plan: WeekSchedule[] = JSON.parse(planRaw);
                setPlan(plan);
            }
            const blocksRaw = sessionStorage.getItem(PENDING_BLOCKS_KEY);
            if (blocksRaw) {
                const blocks: TrainingBlock[] = JSON.parse(blocksRaw);
                setBlocks(blocks);
            }
        } catch (e) {
            console.error('Failed to restore onboarding plan:', e);
        }
        sessionStorage.removeItem(PENDING_PLAN_KEY);
        sessionStorage.removeItem(PENDING_BLOCKS_KEY);
        setOpen(false);
    };

    if (!open) return null;

    // Summarize the existing plan for context
    const existingWeeks = currentPlan?.length || 0;
    const existingWorkouts = currentPlan?.reduce((sum, week) =>
        sum + week.days.filter(d => d.workout || d.secondary).length, 0) || 0;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) handleKeepExisting(); }}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-blue-500" />
                        New plan generated
                    </DialogTitle>
                    <DialogDescription>
                        You already have an active training plan. Would you like to replace it with the newly generated one?
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    {existingWeeks > 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
                            <p className="text-sm text-amber-800 dark:text-amber-200">
                                Your current plan has <span className="font-semibold">{existingWeeks} weeks</span> with{' '}
                                <span className="font-semibold">{existingWorkouts} scheduled workouts</span>.
                                Replacing it will remove all existing workout data.
                            </p>
                        </div>
                    )}

                    <div className="rounded-lg border p-3 bg-muted/30">
                        <p className="text-sm text-muted-foreground">
                            Only your <span className="font-medium text-foreground">training plan</span> will be replaced.
                            All other profile settings, preferences, and integrations will remain unchanged.
                        </p>
                    </div>

                    <div className="flex items-start gap-2 text-xs text-muted-foreground">
                        <Settings className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <p>
                            You can always regenerate your plan from the <span className="font-medium">ATP tab</span> in Settings,
                            or re-run the onboarding from your Dashboard.
                        </p>
                    </div>
                </div>

                <DialogFooter className="flex-col gap-2 sm:flex-row">
                    <Button
                        variant="outline"
                        onClick={handleKeepExisting}
                        className="w-full sm:w-auto gap-2"
                    >
                        <Shield className="w-4 h-4" />
                        Keep existing plan
                    </Button>
                    <Button
                        onClick={handleUseNewPlan}
                        className="w-full sm:w-auto gap-2"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Use new plan
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
